import React, { memo, useState, useCallback, useMemo } from "react";
import JsCookie from "js-cookie";
import { useT, useCtx, WithReduxHocPropsType, withRedux } from "@al/next/al-ex";
import { EventType } from "@al/event-tracking-sensor-analytics";
import { PriceTools, CountryCode } from "@al/base-lib";

import { infinityCookieOptions } from "~/common/dict/base";
import { COOKIE_KEYS_MAP } from "~/common/constants";
import AsyncLoadDataAddress, { SelectAddressItem } from "~/components/ui/AsyncLoadAddress";
import { tracker } from "~/common/tracker";

import { getTrackerSaReportInfo } from "../../../../sensor-config";
import redux from "../../../redux";
import FunctionItem from "../item";
import {
  AddressItemType,
  FreightInfoType,
  PresellSupportType,
  SaleStockInfoType,
  SkuInfoType,
  SellStatusType,
} from "../../../types";

import styles from "./styles.scss";

interface Props extends WithReduxHocPropsType {
  addressList: AddressProvinceItemType[];
  defaultAddress: AddressItemType;
  freightInfo: FreightInfoType;
  presellSupport: PresellSupportType;
  saleStockInfos: SaleStockInfoType[];
  currentSku: SkuInfoType;
}

export interface AddressProvinceItemType {
  name: string;
  id: string;
  children: [];
}

const DeliverTo: React.FC<Props> = (props) => {
  const t = useT();
  const ctx = useCtx();
  const { freightInfo, addressList = [], defaultAddress, currentSku, saleStockInfos } = props;
  const [visible, setVisible] = useState(false);

  const list: Array<{ label: string; value: string }> = addressList?.map((item) => {
    return { label: item.name, value: item.id, children: [] };
  });

  const showPopup = useCallback((): void => {
    setVisible(true);
    tracker.expose({
      sa: {
        event: EventType.PopView,
        ...getTrackerSaReportInfo({ page: "homePage", popName: "addressPop" }),
      },
    });
  }, []);

  const closePopup = useCallback((): void => {
    setVisible(false);
    tracker.popClick({
      sa: getTrackerSaReportInfo({ page: "homePage", popName: "addressPop", eventName: "clickClose" }),
    });
  }, []);

  const onAddressItemSelect = useCallback(
    (item: SelectAddressItem): void => {
      const addressItem = (item as unknown) as Partial<AddressItemType>;
      if (addressItem) {
        const {
          provinceId = "",
          cityId = "",
          districtId = "",
          villageId = "",
          provinceName = "",
          cityName = "",
          districtName = "",
          villageName = "",
        } = addressItem;
        JsCookie.set(
          `${COOKIE_KEYS_MAP.previousSelectAddress}_${ctx.locale.countryId}`,
          `${provinceId}_${cityId}_${districtId}_${villageId}`,
          infinityCookieOptions
        );
        JsCookie.set(
          `${COOKIE_KEYS_MAP.previousSelectAddressLabel}_${ctx.locale.countryId}`,
          `${provinceName}_${cityName}_${districtName}_${villageName}`,
          infinityCookieOptions
        );
        // 持久化选中地址
        props.dispatch(
          redux.actions.setDefaultAddress({ ...addressItem, countryId: ctx.locale.countryId } as AddressItemType)
        );
        // 刷新 sku-price 和 运费
        props.dispatch(redux.actions.updateSkuPrice({ count: 1, isFirst: true }));
      }
    },
    [addressList, ctx]
  );

  // 0 邮费显示包邮，有邮费显示配送至
  const title = useMemo(() => (freightInfo !== null && Number(freightInfo?.freight) === 0 ? "包邮" : "配送至"), [
    freightInfo,
  ]);

  const currentSkuSellStatus = useMemo(
    () => saleStockInfos?.find((item) => item.skuId === currentSku?.skuId)?.sellStatus,
    [currentSku, saleStockInfos]
  );

  return (
    <>
      <FunctionItem
        title={t(title)}
        subTitleClassName={styles.text}
        onClickTitle={showPopup}
        subTitle={`
          ${defaultAddress?.provinceName || ""},
          ${defaultAddress?.cityName || ""},
          ${defaultAddress?.districtName || ""},
          ${defaultAddress?.villageName || ""}
        `}
        bottomContent={
          // 没有运费信息，该模块不展示。
          freightInfo !== null && (
            <div className={styles.deliver_box}>
              <div className={styles.deliver_fee}>
                {t("邮费")}:{" "}
                {PriceTools.formatPrice(ctx.locale.countryCode as CountryCode, freightInfo?.freight || 0, "price")}
              </div>
              {currentSkuSellStatus === SellStatusType.PreSale && props.presellSupport === PresellSupportType.Yes ? (
                <div className={styles.deliver_tip}>{t("10日内发货")}</div>
              ) : null}
            </div>
          )
        }
      />
      <AsyncLoadDataAddress
        visible={visible}
        onClose={closePopup}
        addressList={list}
        onSelectItem={onAddressItemSelect}
      />
    </>
  );
};

export default withRedux((state) => ({
  freightInfo: state.static.freightInfo,
  currentSku: state.static.currentSku,
  defaultAddress: state.static.defaultAddress,
  saleStockInfos: state.static.saleStockInfos,
}))(memo(DeliverTo));
